import './index.scss';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TextFieldProps } from '@material-ui/core';
import { Calendar, useStaticState } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Dialog, DialogActions, DialogContent, Input, InputClasses } from '@mui/material';
import { Moment } from 'moment';
import cx from 'classnames';
import { DateType } from '@date-io/type';
import noop from 'lodash/noop';
import { translate } from '@utils';
import { Button } from '../Button/Button';
import { LangEnum } from '@enums';

interface IDatePickerProps {
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  value: DateType;
  onChange: (value: DateType) => void;
  // TODO: Define schema type
  schema?: any;
  size?: TextFieldProps['size'];
  variant?: TextFieldProps['variant'];
  minDate?: DateType;
  maxDate?: DateType;
  testId?: string;
  lang?: LangEnum;
}

const classes: Partial<InputClasses> = { underline: 'ds-datepicker-input-underline' };

const formatInputDate = (date: Moment): string => (date ? date?.format('DD.MM.yyyy') : '');

export const DatePicker = ({
  placeholder,
  error = false,
  disabled = false,
  value,
  onChange,
  schema,
  size,
  variant,
  minDate,
  maxDate,
  testId,
  lang = LangEnum.ENG,
}: IDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<MaterialUiPickersDate>(null);
  const { properties } = schema
    ? schema
    : {
        properties: { size: size, variant: variant, placeholder: placeholder },
      };

  const handleChange = (newDate: MaterialUiPickersDate): void => {
    if (pathname.includes('answers')) {
      setSelectedDate(newDate);
      onChange(newDate);
      closeDialog();
    } else {
      setSelectedDate(newDate);
      onChange(newDate);
    }
  };

  const { pickerProps } = useStaticState({
    value: selectedDate,
    onChange: handleChange,
  });

  const openDialog = useCallback(() => {
    setSelectedDate(value);
    setOpen(true);
  }, [open, value]);

  const closeDialog = () => setOpen(false);

  const saveValue = () => {
    if (!selectedDate) {
      onChange(pickerProps.date);
    } else {
      onChange(selectedDate);
    }
    closeDialog();
  };

  const resetValue = () => {
    closeDialog();
    onChange(null);
  };

  const handledOpenDialog = !disabled ? openDialog : noop;

  const { pathname } = useLocation();
  const dataTestId =
    testId || schema?.key ? 'ds-datepicker-container' : `ds-datepicker-container-${schema?.key}`;
  return pathname.includes('answers') ? (
    <>
      <div
        className={cx('ds-datepicker-container', { error: error, disabled, active: open })}
        data-testid={dataTestId}
      >
        <Input
          {...properties}
          onClick={handledOpenDialog}
          value={formatInputDate(value)}
          color="secondary"
          disabled={disabled}
          classes={classes}
          readOnly
          disableUnderline
        />
      </div>
      <Dialog
        open={open}
        onClose={closeDialog}
        className="ds-datepicker-dialog-container"
        disableEnforceFocus
      >
        <DialogContent className="ds-datepicker-dialog-content">
          <Calendar
            {...pickerProps}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button type="tertiary-black" onClick={resetValue}>
            {translate('clear').toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : (
    <>
      <div
        className={cx('ds-datepicker-container', { error: error, disabled, active: open })}
        data-testid={dataTestId}
      >
        <Input
          {...properties}
          onClick={handledOpenDialog}
          value={formatInputDate(value)}
          color="secondary"
          disabled={disabled}
          classes={classes}
          readOnly
          disableUnderline
        />
      </div>
      <Dialog
        open={open}
        onClose={closeDialog}
        className="ds-datepicker-dialog-container"
        disableEnforceFocus
      >
        <DialogContent className="ds-datepicker-dialog-content">
          <Calendar {...pickerProps} />
        </DialogContent>
        <DialogActions>
          <Button type="tertiary-black" onClick={saveValue}>
            {translate('save', lang).toUpperCase()}
          </Button>
          <Button type="tertiary-black" onClick={resetValue}>
            {translate('clear', lang).toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
